/* eslint-disable jsx-a11y/alt-text */
/**
=========================================================
* Trust Cargo - Posts
=========================================================

* Adding and editing both happen in a dialog over this list.
*
* Two things this page used to get wrong: Edit navigated to /edit_user/{id},
* and Delete posted to /remove_user - so both acted on the USER whose id
* matched the post. They now use the post's own endpoints.
*/

import React, { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import { Table, TableCell, TableHead, TableBody, TableRow } from "@mui/material";
import axios from "axios";

// Soft UI Dashboard React components
import Can from "components/Can";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CargoLoader from "components/CargoLoader";
import FilterBar, { filterRows } from "components/FilterBar";
import Pagination, { usePagination } from "components/Pagination";
import IdCell, { IdHead } from "components/IdCell";
import AddDialog from "components/AddDialog";
import RowActions from "components/RowActions";

// Soft UI Dashboard React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";

const API = "https://trust-cargo.co/anas_backend/api";

/** post bodies come back as editor HTML; the table shows the text only */
const plain = (html) =>
  String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();

function Posts() {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  // the whole row, not just the id - /get_specefic_post/{id} errors on the
  // server, so the dialog is prefilled from what the list already has
  const [editing, setEditing] = useState(null);

  const fetchData = async () => {
    try {
      const response = await fetch(`${API}/posts`);
      const data = await response.json();
      setRows(data.posts || []);
    } catch (e) {
      setRows([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, []);

  const filteredRows = filterRows(rows, query);
  const pager = usePagination(filteredRows);

  const removeOne = async (row) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Delete this post?")) return;

    try {
      await axios.post(
        `${API}/delete_post`,
        { id: row.id },
        { headers: { Accept: "application/json" } }
      );
      await fetchData();
    } catch (e) {
      // eslint-disable-next-line no-alert
      window.alert(e?.response?.data?.message || "Could not delete");
    }
  };

  const cell = { fontSize: 14 };

  return (
    <DashboardLayout>
      <SuiBox py={1}>
        <SuiBox mb={3}>
          <Card>
            <FilterBar
              title="Posts"
              value={query}
              onChange={setQuery}
              count={filteredRows.length}
              total={rows.length}
              placeholder="Search posts..."
              actions={
                <Can do="create">
                  <Button variant="contained" color="primary" onClick={() => setAdding(true)}>
                    Add Post
                  </Button>
                </Can>
              }
            />

            {loading ? (
              <CargoLoader label="Loading posts" />
            ) : (
              <>
                <SuiBox sx={{ overflowX: "auto" }}>
                  <Table sx={{ minWidth: 800 }} aria-label="posts table">
                    <TableHead>
                      <TableRow>
                        <IdHead />
                        <TableCell align="center">Body</TableCell>
                        <TableCell align="center">Images</TableCell>
                        <TableCell align="center">Operations</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {pager.pageRows.map((row) => (
                        <TableRow key={row.id}>
                          <IdCell value={row.id} />
                          <TableCell align="center" sx={{ ...cell, maxWidth: 420 }}>
                            {plain(row.body) || "-"}
                          </TableCell>

                          <TableCell align="center" sx={cell}>
                            <SuiBox
                              display="flex"
                              gap={1}
                              justifyContent="center"
                              sx={{ overflowX: "auto", py: 0.5 }}
                            >
                              {(row.images || []).map((item) => (
                                <SuiBox
                                  key={item.id}
                                  component="img"
                                  src={item.image}
                                  sx={({ borders }) => ({
                                    width: 96,
                                    height: 72,
                                    objectFit: "cover",
                                    borderRadius: borders.borderRadius.md,
                                    flexShrink: 0,
                                  })}
                                />
                              ))}
                              {(row.images || []).length === 0 && "-"}
                            </SuiBox>
                          </TableCell>

                          <TableCell align="center" sx={cell}>
                            <RowActions
                              actions={[
                                {
                                  icon: "edit",
                                  label: "Edit post",
                                  onClick: () => setEditing(row),
                                },
                                {
                                  icon: "delete",
                                  label: "Delete",
                                  tone: "danger",
                                  onClick: () => removeOne(row),
                                },
                              ]}
                            />
                          </TableCell>
                        </TableRow>
                      ))}

                      {filteredRows.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                            <SuiTypography variant="button" color="text">
                              No posts
                            </SuiTypography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </SuiBox>
                <Pagination {...pager} />
              </>
            )}
          </Card>
        </SuiBox>
      </SuiBox>

      <AddDialog
        open={adding}
        entity="post"
        onClose={() => setAdding(false)}
        onCreated={fetchData}
      />

      {/* same dialog, prefilled from the row. Any images picked here are
          added to the post - the server appends rather than replacing */}
      <AddDialog
        open={Boolean(editing)}
        entity="post"
        recordId={editing?.id || null}
        record={editing}
        onClose={() => setEditing(null)}
        onCreated={fetchData}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default Posts;
